import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { Chart, ChartConfiguration } from "chart.js/auto";
import { ReplaySubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "rask-chart",
    templateUrl: "./chart.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./chart.component.scss"]
})
export class ChartComponent implements OnDestroy, OnChanges, AfterViewInit {
    canvas: any;
    @Output() chartClick = new EventEmitter<any>();
    chartId = `rask-chart-${ChartComponent.chartCount++}`;
    chartInstance: Chart;
    @Input() chartObject: ChartConfiguration;
    componentReady = new ReplaySubject<void>(1);
    ctx: any;

    private readonly destroy$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);

    // eslint-disable-next-line sonarjs/public-static-readonly
    static chartCount = 1;

    constructor() {
        Chart.defaults.font.family = "Roboto";
    }

    getOptions() {
        const options = { ...this.chartObject.options };
        options.onHover = (event, activeElements) => {
            if (activeElements && activeElements.length > 0) {
                this.canvas.style.cursor = "pointer";
            } else {
                this.canvas.style.cursor = "default";
            }
        };
        options.events = [
            "mousemove",
            "mouseup"
        ];
        return options;
    }

    initNewInstance = () => {
        const options = this.getOptions();
        this.chartInstance = new Chart(this.ctx,
            {
                data: { ...this.chartObject.data },
                options: options,
                type: this.chartObject.type,
                plugins: this.chartObject.plugins
            });
    };

    reset() {
        if (this.ctx) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }

        if (this.chartInstance) {
            this.chartInstance.destroy();
        }
    }

    drawChart = () => {
        this.ngZone.runOutsideAngular(_ => {
            this.reset();
            requestAnimationFrame(this.initNewInstance);
        });
    };

    ngAfterViewInit(): void {
        this.canvas = document.getElementById(this.chartId);
        this.ctx = this.canvas.getContext("2d");
        this.componentReady.next();

        this.canvas.addEventListener("click", (event, activeElements) => {
            this.chartClick.emit({
                event: event,
                activeElements: activeElements,
                chart: this.chartInstance
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.chartObject) {
            return;
        }
        this.componentReady.pipe(
            takeUntil(this.destroy$)
        ).subscribe(this.drawChart);
    }

    ngOnDestroy(): void {
        if (this.chartInstance) {
            this.chartInstance.destroy();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }
}
